import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiUser, FiMapPin, FiMail, FiPhone } from "react-icons/fi";
import { FaTruck } from "react-icons/fa";
import PageCreateHeader from "../../components/PageCreateHeader";

const EditDistributor = () => {
  const { id } = useParams(); // Ambil ID dari parameter URL
  const navigate = useNavigate(); // Untuk redirect setelah update

  const [formData, setFormData] = useState({
    name: "",
    kota: "",
    alamat: "",
    email: "",
    telepon: "",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State untuk modal berhasil

  // Ambil data distributor berdasarkan ID
  useEffect(() => {
    const fetchDistributor = async () => {
      const token = localStorage.getItem("token"); // Ambil token dari localStorage
      if (!token) {
        setError("Akses ditolak. Silakan login kembali.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/distributor/${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`, // Tambahkan token di header
            },
          }
        );

        if (!response.ok) {
          throw new Error("Gagal mengambil data distributor");
        }

        const data = await response.json();
        setFormData(data.data); // Pastikan data diambil dari response API
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDistributor();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token"); // Ambil token dari localStorage

    if (!token) {
      setError("Akses ditolak. Silakan login kembali.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/distributor/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Tambahkan token di header
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Gagal memperbarui distributor");
      }

      setShowSuccessModal(true); // Tampilkan modal berhasil
    } catch (err) {
      setError(err.message);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    navigate("/enterprise/distributor"); // Redirect ke halaman daftar distributor setelah modal ditutup
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col space-y-6 p-6 w-full min-w-0">
        <PageCreateHeader
          icon={FaTruck}
          title="Edit Distributor"
          description="Memuat data distributor..."
          breadcrumbs={[
            { label: "Enterprise", to: "/" },
            { label: "Distributor", to: "/enterprise/distributor" },
            { label: "Edit Distributor" },
          ]}
        />
        <p className="text-gray-600">Memuat data distributor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col space-y-6 p-6 w-full min-w-0">
        <PageCreateHeader
          icon={FaTruck}
          title="Edit Distributor"
          description="Tidak dapat memuat data"
          breadcrumbs={[
            { label: "Enterprise", to: "/" },
            { label: "Distributor", to: "/enterprise/distributor" },
            { label: "Edit Distributor" },
          ]}
        />
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col space-y-6 p-6 w-full min-w-0">
      <PageCreateHeader
        icon={FaTruck}
        title="Edit Distributor"
        description="Perbarui data distributor"
        breadcrumbs={[
          { label: "Enterprise", to: "/" },
          { label: "Distributor", to: "/enterprise/distributor" },
          { label: "Edit Distributor" },
        ]}
      />

      <div className="p-6 bg-white rounded-2xl shadow-lg border border-gray-100 w-full min-w-0">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6">
        {/* Nama */}
        <div className="flex flex-col gap-2">
          <label className="font-medium text-gray-700 flex items-center gap-2">
            <FiUser className="text-blue-500" />
            Nama
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Masukkan nama distributor"
            required
          />
        </div>

        {/* Kota */}
        <div className="flex flex-col gap-2">
          <label className="font-medium text-gray-700 flex items-center gap-2">
            <FiMapPin className="text-blue-500" />
            Kota
          </label>
          <input
            type="text"
            name="kota"
            value={formData.kota}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Masukkan kota"
            required
          />
        </div>

        {/* Alamat */}
        <div className="flex flex-col gap-2">
          <label className="font-medium text-gray-700 flex items-center gap-2">
            <FiMapPin className="text-blue-500" />
            Alamat
          </label>
          <textarea
            name="alamat"
            value={formData.alamat}
            onChange={handleChange}
            rows="3"
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            placeholder="Masukkan alamat lengkap"
            required
          />
        </div>

        {/* Email */}
        <div className="flex flex-col gap-2">
          <label className="font-medium text-gray-700 flex items-center gap-2">
            <FiMail className="text-blue-500" />
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Masukkan email"
            required
          />
        </div>

        {/* No. HP */}
        <div className="flex flex-col gap-2">
          <label className="font-medium text-gray-700 flex items-center gap-2">
            <FiPhone className="text-blue-500" />
            No. HP
          </label>
          <input
            type="tel"
            name="telepon"
            value={formData.telepon}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Masukkan nomor HP"
            required
          />
        </div>

        {/* Tombol Submit */}
        <div className="flex justify-end w-full">
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 text-white rounded-xl shadow-md hover:bg-blue-700 transition-all font-semibold"
          >
            {formData.id ? "Update" : "Simpan"}
          </button>
        </div>
      </form>
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <h2 className="text-xl font-semibold text-green-600">Berhasil!</h2>
            <p className="mt-4 text-gray-700">Distributor berhasil diperbarui!</p>
            <button
              onClick={closeSuccessModal}
              className="mt-4 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditDistributor;
